import { Box, Text } from '@user-portal/wikr-components';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';

import { selectCancellationCandidates } from 'store/subscriptions/selectors';

import { sendAnalyticCancelOfferClick, trackScreenLoadCancelOffer } from 'services/Analytics';

import useModals from 'modals/hooks';
import { useSelector } from 'hooks';
import useCancelSubscriptionFlow from '../../../hooks/useCancelSubscriptionFlow';

import { getValuesByKey } from 'helpers/utils';

import { ModalName } from 'modals/types';

import Modal from 'components/Modal/Modal';
import OutlinedButton from 'components/Buttons/OutlinedButton';
import CustomPrimaryButton from 'components/Buttons/CustomPrimaryButton';
import Card from './subComponents/Card';
import FullScreenModalHeader from '../../components/FullScreenModalHeader';
import AutoSlider from '../../components/AutoSlider';

import { CancelFlowOffers, CancelOfferAction, SubscriptionPauseType } from 'types/subscription';

import { getSliderConfig } from './config';

const FreeMonthModal = () => {
    const { t } = useTranslation();
    const { closeModal, openModal } = useModals();

    const cancellationCandidates = useSelector(selectCancellationCandidates);

    const { onCancelSubscription, onRequestPause } = useCancelSubscriptionFlow();

    const subscription_id = getValuesByKey(cancellationCandidates, 'external_id');

    const sliderConfig = getSliderConfig();

    useEffect(() => {
        trackScreenLoadCancelOffer({
            eventLabel: { subscription_id, offer: CancelFlowOffers.FreeMonth },
        });
    }, [subscription_id]);

    const sendAnalytics = useCallback(
        (action: CancelOfferAction) => {
            sendAnalyticCancelOfferClick({
                eventLabel: { subscription_id, action, offer: CancelFlowOffers.FreeMonth },
            });
        },
        [subscription_id]
    );

    const onBackButtonClick = () => {
        sendAnalytics(CancelOfferAction.Close);

        openModal(ModalName.CancellationReasonModal);
    };

    const onClose = useCallback(() => {
        sendAnalytics(CancelOfferAction.Close);

        closeModal();
    }, [closeModal, sendAnalytics]);

    const onSubmit = () => {
        sendAnalytics(CancelOfferAction.Accept);

        onRequestPause(SubscriptionPauseType.FreeMonth);
    };

    const onCancel = () => {
        sendAnalytics(CancelOfferAction.Cancel);

        onCancelSubscription();
    };

    return (
        <Modal fullscreen isOpen customStyles={{ padding: 0 }} withRoundedCorners={false}>
            <FullScreenModalHeader closeModal={onClose} onBackButtonClick={onBackButtonClick} />

            <Box className="container-sm" $paddingTop={24} dataLocator="freeMonthModalA">
                <Text medium mb={12} type="h5">
                    {t('subscription.freeMonthModalNew.title')}
                </Text>
                <Text mb={24}>{t('subscription.freeMonthModalNew.subtitle')}</Text>
                <AutoSlider>
                    {sliderConfig.map(({ img, title, description }) => (
                        <Card img={img} title={title} description={description} key={title} />
                    ))}
                </AutoSlider>

                <CustomPrimaryButton onClick={onSubmit} dataLocator="getOneMonthButton" mb={16}>
                    {t('subscription.freeMonthModalNew.acceptOfferBtn')}
                </CustomPrimaryButton>
                <OutlinedButton
                    onClick={onCancel}
                    text={t('basics.button.cancelSubscription')}
                    backgroundColor="surface-main"
                    dataLocator="cancelSubscriptionButton"
                    mb={32}
                />
            </Box>
        </Modal>
    );
};

export default FreeMonthModal;
